import React, { useMemo } from 'react';
import {
  transformStandardsBiasWorkorderForChart,
  getHeatmapColor
} from '../../../../utils/qaqc/chartDataTransform';
import { PRINT_COLORS, PRINT_CHART_CONFIG } from '../printTheme';

const METRIC_LABELS = {
  meanZ: 'Mean Z-Score',
  pctWarn: '% Warning',
  pctFail: '% Fail',
  zSlopeVsSequence: 'Z-Score Slope'
};

const PrintStandardsHeatmapChart = ({
  data = [],
  selectedElement = 'all',
  metric = 'meanZ',
  minNCrm = 3,
  groupBy = 'element'
}) => {
  const chartData = useMemo(() => {
    return transformStandardsBiasWorkorderForChart(
      data,
      selectedElement === 'all' ? null : selectedElement,
      minNCrm
    );
  }, [data, selectedElement, minNCrm]);

  const heatmapData = useMemo(() => {
    if (chartData.length === 0) return { rows: [], columns: [], cells: {} };

    const rows = new Set();
    const columns = new Set();
    const cells = {};

    chartData.forEach((item) => {
      const rowKey = groupBy === 'element' ? item.element : item.crmCode;
      const colKey = item.workorderId;
      rows.add(rowKey);
      columns.add(colKey);
      const cellKey = `${rowKey}-${colKey}`;
      if (!cells[cellKey]) cells[cellKey] = [];
      cells[cellKey].push(item);
    });

    return {
      rows: Array.from(rows).sort(),
      columns: Array.from(columns).sort(),
      cells
    };
  }, [chartData, groupBy]);

  const getMetricValue = (item) => {
    switch (metric) {
      case 'meanZ':
        return item.meanZ;
      case 'pctWarn':
        return item.pctWarn;
      case 'pctFail':
        return item.pctFail;
      case 'zSlopeVsSequence':
        return item.zSlopeVsSequence;
      default:
        return item.meanZ;
    }
  };

  const getCellColor = (value) => {
    if (value === null || value === undefined) return PRINT_COLORS.backgroundMedium;

    if (metric === 'meanZ') {
      return getHeatmapColor(value);
    }
    if (metric === 'pctWarn' || metric === 'pctFail') {
      if (value >= 20) return '#991b1b';
      if (value >= 10) return '#ef4444';
      if (value >= 5) return '#f59e0b';
      if (value > 0) return '#fbbf24';
      return '#10b981';
    }
    const absVal = Math.abs(value);
    if (absVal >= 0.5) return '#991b1b';
    if (absVal >= 0.3) return '#ef4444';
    if (absVal >= 0.1) return '#f59e0b';
    return '#10b981';
  };

  const formatMetricValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (metric === 'pctWarn' || metric === 'pctFail') {
      return `${value.toFixed(1)}%`;
    }
    return value.toFixed(2);
  };

  const labelMetric = METRIC_LABELS[metric] || metric;

  if (heatmapData.rows.length === 0 || heatmapData.columns.length === 0) {
    return (
      <div style={{ padding: '24px', background: PRINT_COLORS.background }}>
        <h3 style={{ color: PRINT_COLORS.text }}>Standards Bias Heatmap</h3>
        <p style={{ color: PRINT_COLORS.text }}>No data for the selected filters.</p>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', padding: '16px', background: PRINT_COLORS.background }}>
      <h3 style={{ color: PRINT_COLORS.text, marginBottom: '4px', fontSize: '16px', fontWeight: 700 }}>
        Standards Bias Heatmap — {labelMetric}
      </h3>
      <p style={{ color: PRINT_COLORS.text, fontSize: '10px', marginBottom: '12px' }}>
        Element filter: {selectedElement === 'all' ? 'All' : selectedElement} · Group by:{' '}
        {groupBy === 'element' ? 'Element' : 'CRM'} · Min samples: {minNCrm}
      </p>
      <div style={{ overflow: 'visible', background: PRINT_COLORS.background }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: '2px',
            fontSize: `${PRINT_CHART_CONFIG.fontSize.tick}px`
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  background: PRINT_COLORS.backgroundMedium,
                  padding: '6px',
                  color: PRINT_COLORS.text,
                  fontWeight: 700,
                  textAlign: 'left',
                  border: `1px solid ${PRINT_COLORS.border}`
                }}
              >
                {groupBy === 'element' ? 'Element' : 'CRM Code'}
              </th>
              {heatmapData.columns.map((col) => (
                <th
                  key={col}
                  style={{
                    padding: '4px',
                    color: PRINT_COLORS.text,
                    fontWeight: 600,
                    textAlign: 'center',
                    background: PRINT_COLORS.backgroundMedium,
                    border: `1px solid ${PRINT_COLORS.border}`,
                    maxWidth: '72px',
                    fontSize: '8px',
                    verticalAlign: 'bottom'
                  }}
                >
                  <div
                    style={{
                      writingMode: 'vertical-rl',
                      transform: 'rotate(180deg)',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {col}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {heatmapData.rows.map((row) => (
              <tr key={row}>
                <td
                  style={{
                    background: PRINT_COLORS.backgroundMedium,
                    padding: '6px',
                    color: PRINT_COLORS.text,
                    fontWeight: 600,
                    border: `1px solid ${PRINT_COLORS.border}`
                  }}
                >
                  {row}
                </td>
                {heatmapData.columns.map((col) => {
                  const cellKey = `${row}-${col}`;
                  const cellDataArray = heatmapData.cells[cellKey];
                  if (!cellDataArray?.length) {
                    return (
                      <td
                        key={col}
                        style={{
                          padding: '4px',
                          background: PRINT_COLORS.backgroundLight,
                          textAlign: 'center',
                          color: PRINT_COLORS.text,
                          border: `1px solid ${PRINT_COLORS.border}`
                        }}
                      >
                        —
                      </td>
                    );
                  }
                  const cellData = cellDataArray[0];
                  const value = getMetricValue(cellData);
                  const bg = getCellColor(value);
                  const textColor =
                    metric === 'meanZ' && value !== null && Math.abs(value) < 0.3
                      ? PRINT_COLORS.text
                      : '#f9fafb';

                  return (
                    <td
                      key={col}
                      style={{
                        padding: '4px',
                        background: bg,
                        textAlign: 'center',
                        fontWeight: 600,
                        color: textColor,
                        border: `1px solid ${PRINT_COLORS.border}`,
                        fontSize: '9px'
                      }}
                    >
                      <div>{formatMetricValue(value)}</div>
                      <div style={{ fontSize: '8px', opacity: 0.9 }}>n={cellData.nCrm}</div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PrintStandardsHeatmapChart;
